import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import ReactFlow, {
  Background,
  type Node,
  type ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  BlockedTileNode,
  type BlockedTileNodeData,
} from "@/components/maps/blocked-tile-node";
import {
  EntityTokenNode,
  type EntityTokenNodeData,
} from "@/components/maps/entity-token-node";
import {
  PassageMouthNode,
  type PassageMouthNodeData,
} from "@/components/maps/passage-mouth-node";
import { Button } from "@/components/ui/button";
import { GRID_CELL_SIZE } from "@/lib/constants";
import type {
  MapView,
  MapViewEdge,
  MapViewEntity,
} from "@/lib/core/schemas/map-view";
import type { Point } from "@/lib/core/schemas/spatial";

const nodeTypes = {
  entityToken: EntityTokenNode,
  blockedTile: BlockedTileNode,
  passageMouth: PassageMouthNode,
};

/**
 * Tap-to-inspect info card for a passage-mouth marker (mobile-first; the
 * native `title` attr tooltip never fires on touch devices). Anchored to the
 * SpatialGrid container's bottom; surfaces destination zone + direction +
 * traversal kind in a panel that can be tapped to close.
 */
const PassageMouthInfoPopover = ({
  mouth,
  onClose,
}: {
  mouth: PassageMouthNodeData;
  onClose: () => void;
}) => {
  const { destinationLabel, direction, kind, isOutgoing } = mouth;
  const isBidirectional = kind === "bidirectional-passage";
  const arrow = isBidirectional ? "↔" : isOutgoing ? "→" : "←";
  const kindLabel = isBidirectional
    ? "Two-way passage"
    : isOutgoing
      ? "One-way exit"
      : "One-way entry (no walk-back)";

  return (
    <div
      className="absolute right-3 bottom-3 z-10 max-w-xs rounded-md border border-amber-700 bg-amber-50 p-3 shadow-lg dark:bg-amber-950 dark:text-amber-100"
      data-passage-info-popover
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="font-bold text-amber-900 text-base dark:text-amber-100">
            {arrow} {destinationLabel}
          </div>
          {direction && (
            <div className="mt-0.5 text-amber-800 text-xs dark:text-amber-200">
              Direction: <span className="font-mono">{direction}</span>
            </div>
          )}
          <div className="mt-0.5 text-amber-700 text-xs dark:text-amber-300">
            {kindLabel}
          </div>
        </div>
        <Button
          aria-label="Close passage info"
          className="-mr-1 -mt-1 h-6 w-6 p-0 text-amber-900 hover:bg-amber-200 dark:text-amber-100 dark:hover:bg-amber-800"
          onClick={onClose}
          type="button"
          variant="ghost"
        >
          ×
        </Button>
      </div>
    </div>
  );
};

type PassageMouthRender = {
  edgeId: string;
  mouth: { x: number; y: number };
  destinationLabel: string;
  direction: string;
  kind: MapViewEdge["kind"];
  /** True iff this zone is endpointA (source) of the passage. */
  isOutgoing: boolean;
};

type FlowPosition = { x: number; y: number };

const flowPositionToGridPoint = (position: FlowPosition, z = 0): Point => ({
  x: Math.floor(position.x / GRID_CELL_SIZE),
  y: Math.floor(position.y / GRID_CELL_SIZE),
  z,
});

const pointKey = (point: Point): string =>
  `${point.x}:${point.y}:${point.z ?? 0}`;

const createBlockedDestinationSet = (
  blockingEntities: readonly MapViewEntity[]
): ReadonlySet<string> => {
  const keys = new Set<string>();
  for (const entity of blockingEntities) {
    if (!entity.position) {
      continue;
    }
    keys.add(pointKey(entity.position));
  }
  return keys;
};

/**
 * Sub-tile offset (in px) for a marker that shares its tile with N total
 * mouths. Spreads stacked markers in a deterministic diagonal/grid pattern
 * so each remains individually tappable. Returns (0,0) for the unstacked
 * case (one marker on its tile).
 *
 * Pattern (index → offset):
 *   0 → (0, 0)        center
 *   1 → (+14, +14)    SE
 *   2 → (-14, -14)    NW
 *   3 → (+14, -14)    NE
 *   4 → (-14, +14)    SW
 *   5+ → spiral via modular indexing on the 4 corner slots
 *
 * Chosen so 1-5 stacked markers each show a clearly-tappable edge within
 * the 40px cell. Beyond 5 stacking is rare enough to accept partial overlap
 * (the seed pattern is "all passages through one mouth" — N=3 is the
 * common case).
 */
const STACK_OFFSET_CORNERS: ReadonlyArray<{ dx: number; dy: number }> = [
  { dx: 0, dy: 0 },
  { dx: 14, dy: 14 },
  { dx: -14, dy: -14 },
  { dx: 14, dy: -14 },
  { dx: -14, dy: 14 },
];

const stackOffsetPx = (
  index: number,
  total: number
): { dx: number; dy: number } => {
  if (total <= 1) {
    return { dx: 0, dy: 0 };
  }
  return STACK_OFFSET_CORNERS[index % STACK_OFFSET_CORNERS.length];
};

/**
 * Compute the passage mouths to render on the currently-selected zone's grid.
 *
 * For each passage / bidirectional-passage edge incident to `zoneId`:
 * - If this zone is the source (endpointA), surface `mouthA` — the tile
 *   where the passage opens within THIS zone's coordinate space. Destination
 *   is the target node.
 * - If this zone is the target (endpointB), surface `mouthB`. Destination
 *   is the source node.
 *
 * Both directions render a marker regardless of one-way vs bidirectional —
 * the physical door exists in both zones either way. The `isOutgoing` flag
 * tells the marker whether traversal-forward leaves this zone (arrow points
 * outward) or arrives here (arrow points inward); bidirectional ignores it.
 */
const projectMouthsForZone = (
  mapView: MapView,
  zoneId: string
): PassageMouthRender[] => {
  const labelById = new Map(mapView.nodes.map((n) => [n.id, n.label]));
  const result: PassageMouthRender[] = [];
  for (const edge of mapView.edges) {
    // Two independent checks (not `else if`) — a self-loop passage where
    // source === target === zoneId must render BOTH the source-side and
    // target-side markers. The substrate doesn't forbid endpointA === endpointB
    // (no self-loop validator on passage / bidirectional-passage), so the
    // renderer must be total over that case.
    if (edge.source === zoneId) {
      result.push({
        edgeId: edge.id,
        mouth: { x: edge.mouthA.x, y: edge.mouthA.y },
        destinationLabel: labelById.get(edge.target) ?? edge.target,
        direction: edge.directionForward ?? "",
        kind: edge.kind,
        isOutgoing: true,
      });
    }
    if (edge.target === zoneId) {
      result.push({
        edgeId: edge.id,
        mouth: { x: edge.mouthB.x, y: edge.mouthB.y },
        destinationLabel: labelById.get(edge.source) ?? edge.source,
        // Reverse-direction label for bidirectional; for one-way passage
        // the reverse direction doesn't exist (handled by isOutgoing arrow).
        direction:
          edge.kind === "bidirectional-passage"
            ? (edge.directionReverse ?? "")
            : (edge.directionForward ?? ""),
        kind: edge.kind,
        isOutgoing: false,
      });
    }
  }
  return result;
};

export type SpatialGridProps = {
  mapView: MapView;
  zoneId: string | null;
  activeDestinationPick?: { readonly z?: number } | null;
  isResolvingDestination?: boolean;
  onChooseDestination?: (destination: Point) => void;
  onSelectEntity: (entityId: string) => void;
  onSwitchView: (mode: "graph" | "spatial") => void;
};

export const SpatialGrid = ({
  mapView,
  zoneId,
  activeDestinationPick = null,
  isResolvingDestination = false,
  onChooseDestination,
  onSelectEntity,
  onSwitchView,
}: SpatialGridProps) => {
  // Selected passage-mouth (tap-to-inspect, mobile-first). Stores the
  // SpatialGrid-internal node id (e.g. "mouth-<edgeId>-out") which uniquely
  // identifies one side of a passage; null when no mouth is selected.
  const [selectedMouthNodeId, setSelectedMouthNodeId] = useState<string | null>(
    null
  );

  const { tokens, blockedTiles, destinationBlockers, mouths } = useMemo(() => {
    if (zoneId === null) {
      return {
        tokens: [],
        blockedTiles: [],
        destinationBlockers: [],
        mouths: [] as PassageMouthRender[],
      };
    }
    const inZone = mapView.entities.filter((e) => e.locatedIn === zoneId);
    return {
      tokens: inZone.filter(
        (e) =>
          e.components.gridPresence === true &&
          e.components.spatialPosition === true
      ),
      blockedTiles: inZone.filter(
        (e) =>
          e.components.blocksMovement === true &&
          e.components.spatialPosition === true &&
          e.components.gridPresence !== true
      ),
      destinationBlockers: inZone.filter(
        (e) =>
          e.components.blocksMovement === true &&
          e.components.spatialPosition === true
      ),
      mouths: projectMouthsForZone(mapView, zoneId),
    };
  }, [mapView, zoneId]);

  const blockedDestinationKeys = useMemo(
    () => createBlockedDestinationSet(destinationBlockers),
    [destinationBlockers]
  );

  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);

  const handlePaneClick = useCallback(
    (event: MouseEvent | ReactMouseEvent<Element>) => {
      setSelectedMouthNodeId(null);

      if (
        event.target instanceof Element &&
        event.target.closest(".react-flow__node") !== null
      ) {
        return;
      }

      if (
        activeDestinationPick === null ||
        onChooseDestination === undefined ||
        isResolvingDestination ||
        reactFlowInstance === null
      ) {
        return;
      }

      const flowPosition = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const destination = flowPositionToGridPoint(
        flowPosition,
        activeDestinationPick.z ?? 0
      );
      if (blockedDestinationKeys.has(pointKey(destination))) {
        return;
      }

      onChooseDestination(destination);
    },
    [
      activeDestinationPick,
      blockedDestinationKeys,
      isResolvingDestination,
      onChooseDestination,
      reactFlowInstance,
    ]
  );

  if (zoneId === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground">
        <p>No zone selected. Pick a location from the Graph view.</p>
        <Button
          onClick={() => onSwitchView("graph")}
          type="button"
          variant="outline"
        >
          ← Switch to Graph
        </Button>
      </div>
    );
  }

  if (tokens.length === 0 && blockedTiles.length === 0 && mouths.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Empty zone — no positioned entities
      </div>
    );
  }

  const nodes: Node<
    EntityTokenNodeData | BlockedTileNodeData | PassageMouthNodeData
  >[] = [
    ...blockedTiles.map<Node<BlockedTileNodeData>>((e) => ({
      id: `blocked-${e.id}`,
      type: "blockedTile",
      position: {
        x: (e.position?.x ?? 0) * GRID_CELL_SIZE,
        y: (e.position?.y ?? 0) * GRID_CELL_SIZE,
      },
      data: { entity: e },
      draggable: false,
      selectable: false,
    })),
    ...mouths.map<Node<PassageMouthNodeData>>((m) => {
      // Spread stacked markers (same {x,y}) within the cell so each is
      // tappable. Tavern seed shares mouthA at (3,3) across 3 passages —
      // without offset, only the top marker is reachable.
      const tileKey = `${m.mouth.x},${m.mouth.y}`;
      const sameTile = mouths.filter(
        (other) => `${other.mouth.x},${other.mouth.y}` === tileKey
      );
      const stackIndex = sameTile.findIndex(
        (other) =>
          other.edgeId === m.edgeId && other.isOutgoing === m.isOutgoing
      );
      const { dx, dy } = stackOffsetPx(stackIndex, sameTile.length);
      return {
        id: `mouth-${m.edgeId}-${m.isOutgoing ? "out" : "in"}`,
        type: "passageMouth",
        position: {
          x: m.mouth.x * GRID_CELL_SIZE + dx,
          y: m.mouth.y * GRID_CELL_SIZE + dy,
        },
        data: {
          edgeId: m.edgeId,
          destinationLabel: m.destinationLabel,
          direction: m.direction,
          kind: m.kind,
          isOutgoing: m.isOutgoing,
        },
        draggable: false,
        selectable: true,
      };
    }),
    ...tokens.map<Node<EntityTokenNodeData>>((e) => ({
      id: e.id,
      type: "entityToken",
      position: {
        x: (e.position?.x ?? 0) * GRID_CELL_SIZE,
        y: (e.position?.y ?? 0) * GRID_CELL_SIZE,
      },
      data: { entity: e },
      draggable: false,
    })),
  ];

  const selectedMouth =
    selectedMouthNodeId === null
      ? null
      : ((nodes.find((n) => n.id === selectedMouthNodeId)?.data as
          | PassageMouthNodeData
          | undefined) ?? null);

  return (
    <div className="relative" style={{ width: "100%", height: "100%" }}>
      <ReactFlow
        edges={[]}
        elementsSelectable
        fitView
        nodes={nodes}
        nodesConnectable={false}
        nodesDraggable={false}
        nodeTypes={nodeTypes}
        onInit={setReactFlowInstance}
        onNodeClick={(_evt, node) => {
          if (node.type === "entityToken") {
            setSelectedMouthNodeId(null);
            onSelectEntity(node.id);
          } else if (node.type === "passageMouth") {
            // Toggle: tapping the same mouth again closes the popover.
            setSelectedMouthNodeId((prev) =>
              prev === node.id ? null : node.id
            );
          } else {
            setSelectedMouthNodeId(null);
          }
        }}
        onPaneClick={handlePaneClick}
      >
        <Background gap={GRID_CELL_SIZE} />
      </ReactFlow>
      {selectedMouth && (
        <PassageMouthInfoPopover
          mouth={selectedMouth}
          onClose={() => setSelectedMouthNodeId(null)}
        />
      )}
    </div>
  );
};
